// Re-check linter status after fixing @types/react versions
import React, { useState, useEffect, FC, ChangeEvent, SyntheticEvent } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Tooltip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Key as KeyIcon,
  LockOpen as LockOpenIcon,
  Lock as LockIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  StarOutline as StarOutlineIcon,
  Star as StarIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import apiServices from '../../services/api/index';
import { Pool } from '../../types/domain';
import { Administrator, AdministratorCreate, AdministratorUpdate, AdministratorPasswordChange, AdminSelfPasswordChange } from '../../types/auth';
import { SelectChangeEvent } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`password-tabpanel-${index}`}
      aria-labelledby={`password-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminPasswordSection: React.FC<{
  isMainAdmin: boolean; // props로 메인 관리자 여부 전달받음
}> = ({ isMainAdmin }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // 메인 관리자 여부를 로깅 (디버깅용)
  useEffect(() => {
    console.log('AdminPasswordSection: 메인 관리자 여부 (props):', isMainAdmin);
  }, [isMainAdmin]);

  const handleToggleEdit = () => {
    setIsEditing(!isEditing);
    if (!isEditing) {
      setPassword('');
      setConfirmPassword('');
      setError(null);
      setSuccess(null);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    // 메인 관리자인지 확인
    if (!isMainAdmin) {
      setError('메인 관리자만 비밀번호를 변경 가능');
      setLoading(false);
      return;
    }

    // 새 비밀번호 및 확인 비밀번호 검증
    if (password.trim() === '') {
      setError('비밀번호 필수');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('비밀번호가 일치하지 않음');
      setLoading(false);
      return;
    }

    try {
      // 현재 비밀번호 검증 우회를 위해 null 전달
      console.log('관제센터 비밀번호 변경 요청 (현재 비밀번호 무시):', { newPassword: password });
      // 수정: authApi.changeAdminPassword 호출 - 첫 번째 매개변수를 null로 전달
      const response = await apiServices.authApi.changeAdminPassword({ currentPassword: '', newPassword: password }); 
      console.log('비밀번호 변경 응답:', response);

      if (response.success) {
        setSuccess('비밀번호가 변경 완료');
        setIsEditing(false);
        setPassword('');
        setConfirmPassword('');
      } else {
        setError(response.message || '비밀번호 변경에 실패');
      }
    } catch (error) {
      console.error('비밀번호 변경 오류:', error);
      setError('비밀번호 저장 중 오류 발생');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LockIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">관제센터 접근 비밀번호</Typography>
          </Box>
          {/* 메인 관리자인 경우에만 변경/설정 버튼 표시 */}
          {isMainAdmin && !isEditing && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleToggleEdit}
            >
              변경/설정
            </Button>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          관제센터에 접근하기 위한 비밀번호를 설정하거나 변경. 이 비밀번호는 중앙 관제 시스템에 로그인할 때 사용됨.
          {!isMainAdmin && <Box component="span" sx={{ color: 'error.main', fontWeight: 'bold', ml: 1 }}>메인 관리자만 비밀번호 변경 가능.</Box>}
        </Typography>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        
        {isEditing ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="새 비밀번호"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="비밀번호 확인"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={handleToggleEdit}
                >
                  취소
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={loading ? <CircularProgress size={24} /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={loading}
                >
                  저장
                </Button>
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ mt: 2 }}>
             <Typography variant="body1">
               비밀번호를 설정하거나 변경하려면 '변경/설정' 버튼을 클릭.
             </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Add an interface to track password status
interface PoolPasswordStatus {
  poolId: number;
  hasPassword: boolean;
}

const PoolPasswordsSection: React.FC = () => {
  const [pools, setPools] = useState<Pool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingPoolId, setEditingPoolId] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [passwordStatuses, setPasswordStatuses] = useState<PoolPasswordStatus[]>([]);
  const [poolPasswords, setPoolPasswords] = useState<{[key: number]: string}>({});
  const [loadingPasswords, setLoadingPasswords] = useState<{[key: number]: boolean}>({});
  const [viewPasswordId, setViewPasswordId] = useState<number | null>(null);
  
  useEffect(() => {
    fetchPools();
  }, []);
  
  const fetchPools = async () => {
    setLoading(true);
    try {
      const response = await apiServices.poolApi.getPools();
      if (response.success) {
        setPools(response.data);
        
        // After getting pools, check which ones have passwords set
        const statuses: PoolPasswordStatus[] = [];
        for (const pool of response.data) {
          if (pool.id) {
            // In development mode, assume pools with IDs have passwords
            statuses.push({
              poolId: Number(pool.id),
              hasPassword: pool.code && pool.code.length > 0
            });
          }
        }
        setPasswordStatuses(statuses);
      } else {
        setError(response.message || '시설 목록을 불러오기 실패');
      }
    } catch (error) {
      console.error('Failed to fetch pools:', error);
      setError('시설 목록을 불러오기 실패');
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to check if a pool has a password
  const poolHasPassword = (poolId: number): boolean => {
    const status = passwordStatuses.find(s => s.poolId === poolId);
    return status ? status.hasPassword : false;
  };

  // 비밀번호 조회 함수
  const handleViewPassword = async (pool: Pool) => {
    if (!pool.id) return;
    
    const poolId = Number(pool.id);
    
    if (viewPasswordId === poolId) {
      // 이미 비밀번호를 보고 있는 경우, 숨기기
      setViewPasswordId(null);
      return;
    }
    
    // 이미 로딩된 비밀번호가 있는지 확인
    if (poolPasswords[poolId]) {
      setViewPasswordId(poolId);
      return;
    }
    
    // 비밀번호 로딩 상태 설정
    setLoadingPasswords(prev => ({...prev, [poolId]: true}));
    
    try {
      // 비밀번호 조회 API 호출
      const response = await apiServices.poolPasswordApi.getPoolPasswords(poolId);
      if (response.success && response.data) {
        setPoolPasswords(prev => ({...prev, [poolId]: response.data.password}));
        setViewPasswordId(poolId);
      } else {
        setError('비밀번호를 불러오기 실패');
      }
    } catch (error) {
      console.error('Failed to fetch password:', error);
      setError('비밀번호를 불러오기 실패');
    } finally {
      setLoadingPasswords(prev => ({...prev, [poolId]: false}));
    }
  };
  
  const handleEditPassword = (poolId: number) => {
    setEditingPoolId(poolId);
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setSuccess(null);
    setGeneratedPassword(null);
  };

  const handleCancelEdit = () => {
    setEditingPoolId(null);
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setGeneratedPassword(null);
  };

  const handleSavePassword = async (pool: Pool) => {
    if (newPassword !== confirmPassword) {
      setError('비밀번호가 일치하지 않음');
      return;
    }

    if (newPassword.trim() === '') {
      setError('비밀번호 필수');
      return;
    }

    setLoading(true);
    try {
      const response = await apiServices.poolPasswordApi.updatePoolPasswords(Number(pool.id), newPassword);

      if (response.success) {
        setSuccess(`${pool.name} 시설의 비밀번호가 변경 완료`);
        
        // Update the password status in our local state
        setPasswordStatuses(prev => 
          prev.map(status => 
            status.poolId === pool.id 
              ? { ...status, hasPassword: true }
              : status
          )
        );
        
        // 변경된 비밀번호 상태 업데이트
        setPoolPasswords(prev => ({...prev, [Number(pool.id)]: newPassword}));
        
        setEditingPoolId(null);
        setNewPassword('');
        setConfirmPassword('');
        setGeneratedPassword(null);
      } else {
        setError(`비밀번호 변경 실패: ${response.message}`);
      }
    } catch (error) {
      setError('비밀번호 저장 중 오류 발생');
      console.error('Error saving password:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setGeneratedPassword(password);
    setNewPassword(password);
    setConfirmPassword(password);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (loading && pools.length === 0) {
    return <CircularProgress />;
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          <KeyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          시설 접근 비밀번호
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchPools}
          size="small"
        >
          새로고침
        </Button>
      </Box>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        각 시설에 직접 접근할 때 사용하는 비밀번호를 관리. 관제센터에서는 모든 시설에 비밀번호 없이 접근 가능.
      </Typography>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>코드</TableCell>
              <TableCell>시설 이름</TableCell>
              <TableCell>위치</TableCell>
              <TableCell>상태</TableCell>
              <TableCell width="40%">비밀번호 관리</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pools.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  등록된 시설 없음
                </TableCell>
              </TableRow>
            ) : (
              pools.map((pool) => (
                <TableRow key={pool.id}>
                  <TableCell>{pool.code || `-`}</TableCell>
                  <TableCell>{pool.name}</TableCell>
                  <TableCell>{pool.location}</TableCell>
                  <TableCell>
                    {pool.status === 'active' ? '활성' : 
                     pool.status === 'inactive' ? '비활성' : '유지보수'}
                  </TableCell>
                  <TableCell>
                    {editingPoolId === pool.id ? (
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            size="small"
                            label="새 비밀번호"
                            type={showPassword ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={togglePasswordVisibility}
                                    edge="end"
                                    size="small"
                                  >
                                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            size="small"
                            label="비밀번호 확인"
                            type={showPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={togglePasswordVisibility}
                                    edge="end"
                                    size="small"
                                  >
                                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        {generatedPassword && (
                          <Grid item xs={12}>
                            <Alert severity="info" sx={{ mb: 1 }}>
                              생성된 비밀번호: {generatedPassword}
                            </Alert>
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={generateRandomPassword}
                            >
                              자동 생성
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={handleCancelEdit}
                            >
                              취소
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              onClick={() => handleSavePassword(pool)}
                            >
                              저장
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TextField
                          size="small"
                          type={viewPasswordId === Number(pool.id) ? 'text' : 'password'}
                          value={viewPasswordId === Number(pool.id) && poolPasswords[Number(pool.id)] 
                            ? poolPasswords[Number(pool.id)] 
                            : poolHasPassword(Number(pool.id!)) ? '••••••••' : ''}
                          placeholder={poolHasPassword(Number(pool.id!)) ? "비밀번호 재설정" : "비밀번호 미설정"}
                          InputProps={{
                            readOnly: true,
                            startAdornment: (
                              <InputAdornment position="start">
                                {poolHasPassword(Number(pool.id!)) ? <LockIcon color="primary" /> : <LockOpenIcon color="disabled" />}
                              </InputAdornment>
                            ),
                          }}
                          sx={{ flexGrow: 1, mr: 1 }}
                        />
                        
                        {poolHasPassword(Number(pool.id!)) && (
                          <Tooltip title={viewPasswordId === Number(pool.id) ? "비밀번호 숨기기" : "비밀번호 보기"}>
                            <IconButton 
                              onClick={() => handleViewPassword(pool)}
                              disabled={loadingPasswords[Number(pool.id!)]}
                            >
                              {loadingPasswords[Number(pool.id!)] ? (
                                <CircularProgress size={24} />
                              ) : viewPasswordId === Number(pool.id) ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        <Tooltip title="비밀번호 변경">
                          <IconButton onClick={() => handleEditPassword(Number(pool.id!))}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const AdminAccountsSection: React.FC = () => {
  const { authState } = useAuth();
  const [administrators, setAdministrators] = useState<Administrator[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentAdmin, setCurrentAdmin] = useState<Administrator | null>(null);
  const [isMainAdmin, setIsMainAdmin] = useState<boolean>(false);
  
  // 관리자 추가/수정 관련 상태
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [editingAdmin, setEditingAdmin] = useState<Administrator | null>(null);
  const [newAdmin, setNewAdmin] = useState<AdministratorCreate>({
    username: '',
    password: '',
    name: '',
    role: 'manager',
    isMainAdmin: false
  });
  
  // 비밀번호 변경 관련 상태
  const [openPasswordDialog, setOpenPasswordDialog] = useState<boolean>(false);
  const [passwordChange, setPasswordChange] = useState<AdminSelfPasswordChange>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [selectedAdminId, setSelectedAdminId] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  
  // 관리자 목록 불러오기 함수
  const fetchAdministrators = async () => {
    setLoading(true);
    try {
      console.log('관리자 목록 로딩 시작...');
      const response = await apiServices.administratorApi.getAdministrators();
      if (response.success) {
        console.log('관리자 목록 로딩 성공:', response.data);
        
        // 수신된 관리자 데이터 검증 및 가공
        const processedData = response.data.map(admin => {
          // isMainAdmin 필드가 존재하는지 확인하고, 없으면 명시적으로 false 설정
          const adminWithMainField = {
            ...admin,
            isMainAdmin: admin.isMainAdmin === true // 명시적 boolean 변환
          };
          
          // 데이터 일관성 확인 로깅
          console.log(`관리자 [${admin.id}] ${admin.name}의 isMainAdmin 값:`, adminWithMainField.isMainAdmin);
          
          return adminWithMainField;
        });
        
        // 가공된 데이터로 상태 업데이트
        setAdministrators(processedData);
        
        // 메인 관리자 존재 여부 확인 (가공된 데이터 사용)
        const mainAdmin = processedData.find(admin => admin.isMainAdmin === true);
        const hasMainAdmin = !!mainAdmin;
        console.log('메인 관리자 존재 여부:', hasMainAdmin, mainAdmin ? mainAdmin.id : 'none');
        
        // 현재 접속 중인 관리자가 메인 관리자인지 확인
        const currentUserId = authState.user?.id?.toString();
        if (currentUserId) {
          const currentUserAdmin = processedData.find(
            admin => String(admin.id) === currentUserId
          );
          
          if (currentUserAdmin) {
            console.log('현재 로그인한 관리자 정보:', currentUserAdmin);
            // 현재 관리자 정보 설정 (isMainAdmin 값을 명시적으로 설정)
            setCurrentAdmin(currentUserAdmin);
            setIsMainAdmin(currentUserAdmin.isMainAdmin === true);
          } else {
            console.warn('PasswordManager: 현재 사용자를 관리자 목록에서 찾을 수 없습니다');
          }
        }
        
        // 메인 관리자가 없는 경우에만 메시지 표시
        if (!hasMainAdmin) {
          setSuccess("현재 메인 관리자가 지정되지 않음");
        } else {
          // 메인 관리자가 있지만 현재 사용자가 아닌 경우 메시지 초기화
          setSuccess(null);
        }
      } else {
        console.error('관리자 목록 로딩 실패:', response.message);
        setError(response.message || '관리자 목록을 불러오기 실패');
      }
    } catch (error) {
      console.error('관리자 목록 불러오기 오류:', error);
      setError('관리자 목록을 불러오기 실패');
    } finally {
      setLoading(false);
    }
  };
  
  // 현재 관리자 정보 불러오기 함수
  const fetchCurrentAdmin = async () => {
    try {
      console.log('현재 관리자 정보 로딩 시작...');
      const response = await apiServices.administratorApi.getCurrentAdministrator();
      if (response.success && response.data) {
        console.log('현재 관리자 정보 로드 성공:', response.data);
        setCurrentAdmin(response.data);
        setIsMainAdmin(response.data.isMainAdmin === true);
        return response.data;
      } else {
        console.error('현재 관리자 정보 로드 실패:', response);
      }
    } catch (error) {
      console.error('현재 관리자 정보 불러오기 오류:', error);
    }
    return null;
  };
  
  // 초기 데이터 로딩
  useEffect(() => {
    const initData = async () => {
      // 로딩 상태 설정
      setLoading(true);
      
      // 현재 로그인한 사용자 정보 직접 설정
      const userId = authState.user?.id?.toString();
      const userName = authState.user?.name || '';
      
      if (userId) {
        // 임시 현재 관리자 정보 설정
        const tempAdmin: Administrator = {
          id: userId,
          username: authState.user?.username || '',
          name: userName,
          role: 'admin',
          isMainAdmin: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        setCurrentAdmin(tempAdmin);
      }
      
      // 관리자 목록 로딩
      await fetchAdministrators();
      
      // 로딩 상태 해제
      setLoading(false);
    };
    
    initData();
  }, [authState.user]);
  
  // 현재 로그인한 관리자를 메인 관리자로 설정
  const handleSetCurrentAsMainAdmin = async () => {
    if (!currentAdmin) {
      setError('현재 로그인한 관리자 정보를 찾기 불가');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null); // 기존 메시지 초기화
    
    try {
      console.log('메인 관리자 설정 시작:', currentAdmin);
      const adminId = currentAdmin.id;
      const updateData: AdministratorUpdate = {
        name: currentAdmin.name,
        role: currentAdmin.role,
        isMainAdmin: true,
        email: currentAdmin.email,
        phone: currentAdmin.phone
      };
      
      // API 요청 로그 추가
      console.log('메인 관리자 설정 요청 데이터:', JSON.stringify(updateData));
      
      // 서버에 메인 관리자 설정 요청
      const response = await apiServices.administratorApi.updateAdministrator(Number(adminId), updateData);
      console.log('메인 관리자 설정 응답:', response);
      
      if (response.success) {
        // 성공 메시지 표시
        setSuccess(`${currentAdmin.name} 관리자가 메인 관리자로 지정됨`);
        
        // 로컬 상태 즉시 업데이트
        setIsMainAdmin(true);
        setCurrentAdmin(prevAdmin => {
          if (prevAdmin) {
            return { ...prevAdmin, isMainAdmin: true };
          }
          return prevAdmin;
        });
        
        // 관리자 목록 즉시 업데이트
        setAdministrators(prevAdmins => 
          prevAdmins.map(admin => ({
            ...admin,
            isMainAdmin: admin.id === adminId
          }))
        );
        
        // 백엔드에서 최신 데이터 다시 가져오기 (약간의 지연 후)
        setTimeout(() => {
          fetchAdministrators();
        }, 500);
      } else {
        console.error('메인 관리자 설정 실패:', response.message);
        setError(response.message || '메인 관리자 지정에 실패');
      }
    } catch (error) {
      console.error('메인 관리자 지정 오류:', error);
      setError('메인 관리자 지정 중 오류 발생');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddAdmin = () => {
    setEditingAdmin(null);
    setNewAdmin({
      username: '',
      password: '',
      name: '',
      role: 'manager',
      isMainAdmin: false
    });
    setOpenDialog(true);
  };
  
  const handleEditAdmin = (admin: Administrator) => {
    setEditingAdmin(admin);
    setNewAdmin({
      username: admin.username,
      password: '',  // 비밀번호는 수정 시 비워둡니다
      name: admin.name,
      role: admin.role || 'admin',
      isMainAdmin: admin.isMainAdmin || false,
      email: admin.email || '',
      phone: admin.phone || ''
    });
    console.log('편집할 관리자 정보:', admin);
    console.log('초기화된 폼 데이터:', {
      username: admin.username,
      name: admin.name,
      role: admin.role || 'admin',
      isMainAdmin: admin.isMainAdmin || false,
      email: admin.email || '',
      phone: admin.phone || ''
    });
    setOpenDialog(true);
  };
  
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown; }> | SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    console.log(`필드 변경: ${name}, 값: ${value}`);
    setNewAdmin(prev => ({
      ...prev,
      [name as string]: value,
    }));
  };
  
  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    console.log(`스위치 변경: ${name}, 값: ${checked}`);
    setNewAdmin(prev => ({
      ...prev,
      [name]: checked,
    }));
  };
  
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordChange(prev => ({
      ...prev,
      [name]: value,
    }));
  };
  
  const handleSaveAdmin = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    // 유효성 검사
    if (!newAdmin.username || !newAdmin.name) {
      setError('아이디와 이름 필수 입력 항목');
      setLoading(false);
      return;
    }
    
    if (!editingAdmin && !newAdmin.password) {
      setError('비밀번호 필수 입력 항목');
      setLoading(false);
      return;
    }
    
    try {
      if (editingAdmin) {
        // 관리자 정보 업데이트
        const updateData: AdministratorUpdate = {
          name: newAdmin.name,
          role: newAdmin.role,
          isMainAdmin: newAdmin.isMainAdmin,
          email: newAdmin.email || '',
          phone: newAdmin.phone || ''
        };
        
        console.log('관리자 업데이트 요청 데이터:', JSON.stringify(updateData));
        
        // 비밀번호가 입력된 경우에만 비밀번호 변경 처리
        let passwordChanged = false;
        if (newAdmin.password) {
          try {
            console.log('비밀번호 변경 시도:', editingAdmin.id);
            const passwordResponse = await apiServices.administratorApi.changeAdministratorPassword(Number(editingAdmin.id), {
              newPassword: newAdmin.password
            });
            console.log('비밀번호 변경 응답:', passwordResponse);
            
            if (passwordResponse.success) {
              passwordChanged = true;
              console.log('관리자 비밀번호가 성공적으로 변경되었습니다.');
            } else {
              setError(passwordResponse.message || '비밀번호 변경에 실패');
              setLoading(false);
              return;
            }
          } catch (error) {
            console.error('비밀번호 변경 오류:', error);
            setError('비밀번호 변경에 실패');
            setLoading(false);
            return;
          }
        }
        
        // 원래 메인 관리자 값 저장 (상태 비교용)
        const wasMainAdmin = editingAdmin.isMainAdmin;
        const isMainAdminChanged = wasMainAdmin !== updateData.isMainAdmin;
        
        console.log('관리자 정보 업데이트 요청:', editingAdmin.id, updateData);
        const response = await apiServices.administratorApi.updateAdministrator(Number(editingAdmin.id), updateData);
        console.log('관리자 정보 업데이트 응답:', response);
        
        if (response.success) {
          // 성공 메시지 설정
          setSuccess(
            passwordChanged 
              ? '관리자 정보와 비밀번호가 업데이트 완료' 
              : '관리자 정보가 업데이트 완료'
          );
          
          // 다이얼로그 닫기
          setOpenDialog(false);
          setLoading(false);
          
          // 상태 즉시 업데이트하지 않고 백엔드 데이터만 기다리도록 변경
          // 관리자 목록 새로고침 (약간의 지연으로 백엔드 업데이트 시간 허용)
          setTimeout(async () => {
            console.log('관리자 목록 새로고침 실행');
            await fetchAdministrators();
          }, 300);
        } else {
          setError(response.message || '관리자 정보 업데이트에 실패');
          setLoading(false);
        }
      } else {
        // 새 관리자 생성
        console.log('새 관리자 생성 시작:', newAdmin);
        const response = await apiServices.administratorApi.createAdministrator(newAdmin);
        console.log('새 관리자 생성 응답:', response);
        
        if (response.success) {
          setSuccess('새 관리자가 생성됨');
          setOpenDialog(false);
          
          // 메인 관리자로 지정한 경우, 다른 관리자들의 메인 관리자 상태 업데이트
          if (newAdmin.isMainAdmin) {
            setAdministrators(prevList => 
              prevList.map(admin => ({...admin, isMainAdmin: false}))
            );
          }
          
          // 백엔드에서 최신 데이터 로드
          setTimeout(() => {
            fetchAdministrators();
          }, 500);
        } else {
          setError(response.message || '관리자 생성에 실패');
        }
      }
    } catch (error) {
      console.error('관리자 저장 중 오류:', error);
      setError('관리자 정보 저장 중 오류 발생');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteAdmin = async (admin: Administrator) => {
    if (window.confirm(`정말로 ${admin.name} 관리자를 삭제 확인`)) {
      setLoading(true);
      setError(null);
      
      try {
        const response = await apiServices.administratorApi.deleteAdministrator(Number(admin.id));
        if (response.success) {
          setSuccess('관리자가 삭제 완료');
          await fetchAdministrators(); // 관리자 목록 새로고침
        } else {
          setError(response.message || '관리자 삭제에 실패');
        }
      } catch (error) {
        console.error('Error deleting administrator:', error);
        setError('관리자 삭제 중 오류 발생');
      } finally {
        setLoading(false);
      }
    }
  };
  
  const handleOpenPasswordDialog = (admin: Administrator) => {
    setSelectedAdminId(admin.id);
    setPasswordChange({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setOpenPasswordDialog(true);
  };
  
  const handleClosePasswordDialog = () => {
    setOpenPasswordDialog(false);
    setSelectedAdminId(null);
  };
  
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  const handleSavePassword = async () => {
    if (!selectedAdminId) return;
    
    setLoading(true);
    setError(null);
    
    // 유효성 검사
    if (!passwordChange.currentPassword || !passwordChange.newPassword) {
      setError('모든 비밀번호 항목을 입력');
      setLoading(false);
      return;
    }
    
    if (passwordChange.newPassword !== passwordChange.confirmPassword) {
      setError('새 비밀번호와 확인 비밀번호가 일치하지 않음');
      setLoading(false);
      return;
    }
    
    try {
      const response = await apiServices.administratorApi.changeAdministratorPassword(Number(selectedAdminId), {
        currentPassword: passwordChange.currentPassword,
        newPassword: passwordChange.newPassword
      });
      
      if (response.success) {
        setSuccess('비밀번호가 변경 완료');
        setOpenPasswordDialog(false);
      } else {
        setError(response.message || '비밀번호 변경에 실패');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setError('비밀번호 변경 중 오류 발생');
    } finally {
      setLoading(false);
    }
  };
  
  const canEdit = (admin: Administrator) => {
    return true; // 모든 관리자 수정 가능하도록 변경
  };
  
  const canDelete = (admin: Administrator) => {
    return admin.id !== currentAdmin?.id; // 자기 자신을 제외한 모든 관리자 삭제 가능
  };
  
  // 메인 관리자가 있는지 확인
  const hasMainAdmin = administrators.some(admin => admin.isMainAdmin);
  
  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 1, color: 'error.main', fontSize: 28 }} />
            <Typography variant="h5" color="error.main" fontWeight="bold">관리자 계정 관리</Typography>
          </Box>
          <Box>
            {/* 관리자 추가 버튼 - 모든 사용자에게 표시 */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddAdmin}
              color="primary"
              size="medium"
              sx={{ fontWeight: 'bold', mr: 1 }}
            >
              관리자 추가
            </Button>
          </Box>
        </Box>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        
        {loading && administrators.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>아이디</TableCell>
                  <TableCell>이름</TableCell>
                  <TableCell>역할</TableCell>
                  <TableCell>이메일</TableCell>
                  <TableCell>연락처</TableCell>
                  <TableCell>상태</TableCell>
                  <TableCell>작업</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {administrators.map((admin) => (
                  <TableRow key={admin.id}>
                    <TableCell>{admin.username}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {admin.name}
                        {admin.isMainAdmin && (
                          <Tooltip title="메인 관리자">
                            <StarIcon sx={{ ml: 1, color: 'warning.main', fontSize: 18 }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={admin.role === 'admin' ? '관리자' : '매니저'} 
                        color={admin.role === 'admin' ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{admin.email || '-'}</TableCell>
                    <TableCell>{admin.phone || '-'}</TableCell>
                    <TableCell>
                      {admin.id === currentAdmin?.id ? (
                        <Chip label="현재 접속 중" color="success" size="small" />
                      ) : (
                        <Chip label="일반" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {canEdit(admin) && (
                          <>
                            <Tooltip title="정보 수정">
                              <IconButton 
                                color="primary"
                                onClick={() => handleEditAdmin(admin)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="비밀번호 변경">
                              <IconButton 
                                color="secondary"
                                onClick={() => handleOpenPasswordDialog(admin)}
                              >
                                <LockIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {canDelete(admin) && (
                          <Tooltip title="관리자 삭제">
                            <IconButton 
                              color="error"
                              onClick={() => handleDeleteAdmin(admin)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {administrators.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      등록된 관리자 없음
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
      
      {/* 관리자 추가/수정 다이얼로그 */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAdmin ? '관리자 수정' : '관리자 추가'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="아이디"
                name="username"
                value={newAdmin.username}
                onChange={handleChange}
                disabled={!!editingAdmin}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={editingAdmin ? "새 비밀번호 (변경 없으면 비움)" : "비밀번호"}
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={newAdmin.password}
                onChange={handleChange}
                required={!editingAdmin}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="이름"
                name="name"
                value={newAdmin.name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>역할</InputLabel>
                <Select
                  name="role"
                  value={newAdmin.role}
                  onChange={handleChange as (event: SelectChangeEvent<string>, child: React.ReactNode) => void}
                  label="역할"
                >
                  <MenuItem value="admin">관리자</MenuItem>
                  <MenuItem value="manager">매니저</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="이메일"
                name="email"
                type="email"
                value={newAdmin.email || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="연락처"
                name="phone"
                value={newAdmin.phone || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newAdmin.isMainAdmin || false}
                    onChange={handleSwitchChange}
                    name="isMainAdmin"
                    color="warning"
                  />
                }
                label="메인 관리자로 지정 (기존 메인 관리자는 일반 관리자로 변경됨)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveAdmin}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={24} /> : null}
          >
            저장
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 비밀번호 변경 다이얼로그 */}
      <Dialog open={openPasswordDialog} onClose={handleClosePasswordDialog} maxWidth="sm" fullWidth>
        <DialogTitle>비밀번호 변경</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="현재 비밀번호"
                name="currentPassword"
                type={showPassword ? 'text' : 'password'}
                value={passwordChange.currentPassword}
                onChange={handlePasswordChange}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="새 비밀번호"
                name="newPassword"
                type={showPassword ? 'text' : 'password'}
                value={passwordChange.newPassword}
                onChange={handlePasswordChange}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="새 비밀번호 확인"
                name="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={passwordChange.confirmPassword}
                onChange={handlePasswordChange}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog}>취소</Button>
          <Button 
            variant="contained" 
            onClick={handleSavePassword}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={24} /> : null}
          >
            저장
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

const PasswordManager: React.FC = () => {
  const { authState } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [isMainAdmin, setIsMainAdmin] = useState<boolean>(false); // 다시 false로 초기화
  const [adminData, setAdminData] = useState<Administrator | null>(null);

  // 관리자 정보 로드 함수
  const loadAdminInfo = async () => {
    try {
      console.log('PasswordManager: 관리자 정보 로딩 시작...');
      
      // 현재 로그인한 관리자 확인
      const adminResponse = await apiServices.administratorApi.getCurrentAdministrator();
      
      if (adminResponse.success && adminResponse.data) {
        console.log('PasswordManager: 관리자 정보 로드 성공:', adminResponse.data);
        
        // 관리자 정보와 메인 관리자 상태 설정
        setAdminData(adminResponse.data);
        
        // is_main_admin 또는 isMainAdmin 필드 확인
        const isMain = adminResponse.data.isMainAdmin === true;
        
        console.log('PasswordManager: 메인 관리자 여부 (직접 확인):', isMain);
        console.log('PasswordManager: isMainAdmin 필드:', adminResponse.data.isMainAdmin);
        
        setIsMainAdmin(isMain); // 실제 메인 관리자 상태 사용
      } else {
        console.error('PasswordManager: 관리자 정보 로드 실패:', adminResponse);
        
        // 대체 방법 - 관리자 목록에서 현재 사용자 찾기
        if (authState.user?.id) {
          console.log('PasswordManager: 관리자 목록에서 현재 사용자 검색 시도');
          const adminListResponse = await apiServices.administratorApi.getAdministrators();
          
          if (adminListResponse.success && Array.isArray(adminListResponse.data)) {
            const currentUserAdmin = adminListResponse.data.find(
              admin => String(admin.id) === String(authState.user?.id)
            );
            
            if (currentUserAdmin) {
              console.log('PasswordManager: 관리자 목록에서의 현재 사용자:', currentUserAdmin);
              setAdminData(currentUserAdmin);
              
              // is_main_admin 또는 isMainAdmin 필드 확인
              const isMain = currentUserAdmin.isMainAdmin === true;
              
              console.log('PasswordManager: 관리자 목록에서의 메인 관리자 여부 (직접 확인):', isMain);
              console.log('PasswordManager: isMainAdmin 필드:', currentUserAdmin.isMainAdmin);
              
              setIsMainAdmin(isMain); // 실제 메인 관리자 상태 사용
            } else {
              console.warn('PasswordManager: 현재 사용자를 관리자 목록에서 찾을 수 없습니다');
            }
          } else {
            console.warn('PasswordManager: 관리자 목록을 가져올 수 없습니다');
          }
        }
      }
    } catch (error) {
      console.error('PasswordManager: 관리자 정보 로딩 오류:', error);
    }
  };

  // 컴포넌트 마운트 시 관리자 정보 로드
  useEffect(() => {
    loadAdminInfo();
    
    // 정기적으로 관리자 정보 새로고침 (선택 사항)
    const refreshInterval = setInterval(() => {
      console.log('PasswordManager: 관리자 정보 정기 새로고침');
      loadAdminInfo();
    }, 30000); // 30초마다 새로고침
    
    return () => clearInterval(refreshInterval);
  }, [authState.user?.id]);

  // 상태 변경 감지를 위한 useEffect
  useEffect(() => {
    console.log('PasswordManager: isMainAdmin 상태 변경됨:', isMainAdmin);
  }, [isMainAdmin]);

  // 탭 변경 처리
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // 탭 전환 시 콘솔에 디버깅 정보 출력
    console.log(`탭 전환: ${newValue}, 메인 관리자 여부: ${isMainAdmin}`);
  };

  // 관리자 상태 수동 새로고침 함수
  const refreshAdminStatus = () => {
    console.log('PasswordManager: 관리자 상태 수동 새로고침 요청');
    loadAdminInfo();
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="password management tabs">
          <Tab label="관제센터 접근 비밀번호" />
          <Tab label="시설 비밀번호" />
          <Tab label="관리자 계정" />
        </Tabs>
      </Box>
      
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">관제센터 접근 비밀번호 관리</Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refreshAdminStatus}
            size="small"
          >
            관리자 상태 새로고침
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          관리자 상태: {isMainAdmin ? '메인 관리자' : '일반 관리자'} (상태가 다르면 '관리자 상태 새로고침' 클릭)
        </Typography>
        <AdminPasswordSection isMainAdmin={isMainAdmin} />
      </TabPanel>
      
      <TabPanel value={tabValue} index={1}>
        <PoolPasswordsSection />
      </TabPanel>
      
      <TabPanel value={tabValue} index={2}>
        <AdminAccountsSection />
      </TabPanel>
    </Box>
  );
};

export default PasswordManager; 